import React from 'react';
import { breakpoints } from '@com/core/common/styles/baseStyles';
import { useTranslation } from '@com/core/common/translations/i18n';
import styled from 'styled-components';
import { colors } from '@com/core/noon/styles/noonTheme';

type TProps = {
    cancelText?: string | JSX.Element;
    children: JSX.Element;
    isLoading?: boolean;
    onCancel: () => void;
    onSubmit?: () => void;
    isDisabled?: boolean;
    submitText?: string;
    subtitle?: string;
    title?: string;
    translateCancelText?: boolean;
    translateSubmitText?: boolean;
    translateSubtitle?: boolean;
    translateTitle?: boolean;
    backgroundImage?: string;
    noMargin?: boolean;
};

const DialogModal = ({
    backgroundImage,
    cancelText = 'cancel',
    children,
    isDisabled,
    isLoading = false,
    noMargin = false,
    onCancel,
    onSubmit,

    submitText = 'ok',
    subtitle,
    title,
    translateCancelText = true,
    translateSubmitText = true,
    translateSubtitle = true,
    translateTitle = true,
}: TProps): JSX.Element => {
    const { t } = useTranslation('common');

    let translatedSubtitle = subtitle;
    let translatedTitle = title;
    let translatedCancelText = cancelText;
    let translatedSubmitText = submitText;

    if (translateSubtitle && typeof subtitle === 'string') {
        translatedSubtitle = t(subtitle);
    }

    if (translateTitle && typeof title === 'string') {
        translatedTitle = t(title);
    }

    if (translateCancelText && typeof cancelText === 'string') {
        translatedCancelText = t(cancelText);
    }

    if (translateSubmitText && typeof submitText === 'string') {
        translatedSubmitText = t(submitText);
    }

    return (
        <DialogModalBg>
            <DialogModalWrapper backgroundImage={backgroundImage}>
                <ContentWrapper noMargin={noMargin}>
                    {!!title && (
                        <DialogHeading data-qa={`lbl_modalTitle_${translatedTitle}`}>{translatedTitle}</DialogHeading>
                    )}
                    {!!subtitle && (
                        <DialogSubheading data-qa={`lbl_modalSubTitle_${translatedSubtitle}`}>
                            {translatedSubtitle}
                        </DialogSubheading>
                    )}

                    {children}
                </ContentWrapper>
                <DialogModalFooter>
                    <DialogBtn data-qa={`btn_${translatedCancelText}`} className="cancel" onClick={onCancel}>
                        {translatedCancelText}
                    </DialogBtn>
                    {!!onSubmit && (
                        <DialogBtn
                            data-qa={`btn_${translatedSubmitText}`}
                            className="confirm"
                            onClick={onSubmit}
                            disabled={isDisabled}>
                            {isLoading ? t('loading') : translatedSubmitText}
                        </DialogBtn>
                    )}
                </DialogModalFooter>
            </DialogModalWrapper>
        </DialogModalBg>
    );
};

const DialogModalBg = styled.div`
    display: block;
    background: rgba(0, 0, 0, 0.5);
    position: fixed;
    width: 100%;
    height: 100%;
    left: 0;
    bottom: 0;
    z-index: 3;
`;

type TDialogModalBg = { backgroundImage?: string };
const DialogModalWrapper = styled.div<TDialogModalBg>`
    position: absolute;
    left: 0;
    top: 0;
    width: 90%;
    max-width: 400px;
    display: flex;
    flex-direction: column;
    align-items: center;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 3px;
    background-color: ${colors.bgPrimary};
    ${({ backgroundImage }): string => {
        if (backgroundImage) {
            return `
                background-image: url(${backgroundImage});
                background-size: cover;
                background-repeat: no-repeat;
            `;
        }
        return ``;
    }}
`;

const ContentWrapper = styled.div<{ noMargin: boolean }>`
    ${({ noMargin }): string => {
        if (!noMargin) {
            return `padding: 20px 10px;`;
        }
        return `padding: 20px 0px;`;
    }}
    width: 100%;
    @media ${breakpoints.minTablet} {
        ${({ noMargin }): string => {
            if (!noMargin) {
                return `padding: 20px 15px;`;
            }
            return `padding: 20px 0px;`;
        }}
    }
`;

const DialogHeading = styled.h2`
    font-size: 1.41666rem;
    text-align: center;
    font-weight: 800;
    margin-bottom: 10px;
    color: #404553;
`;

const DialogSubheading = styled.h3`
    font-size: 0.875rem;
    text-align: center;
    margin-bottom: 25px;
    line-height: 1.5;
`;

const DialogModalFooter = styled.div`
    width: 100%;
    border-top: 1px solid ${colors.bodyLight};
    display: flex;
    flex-direction: row;
`;

const DialogBtn = styled.button`
    flex: 1;
    text-align: center;
    font-size: 1.25rem;
    color: ${colors.brandSecondary};
    font-weight: 600;
    padding: 17px 0px;
    color: ${colors.bodyMid};
    &:hover {
        color: ${colors.brandSecondary};
    }
    &.cancel {
        text-transform: uppercase;
        ${({ theme }): string =>
            theme.isRTL
                ? `border-left: 1px solid ${colors.bodyLight};`
                : `border-right: 1px solid ${colors.bodyLight};`}
    }
    &.confirm {
        text-transform: uppercase;
        color: ${colors.brandSecondary};
    }
    &:disabled {
        color: grey;
    }
`;

export default DialogModal;
